import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CritStatItem } from '@/entities/dashboards';
import { cn, QualityZoneColor, zoneCardClass } from '@/shared/lib';
import { criterionKey } from '../model/hiddenCriteria';
import { fmt } from './utils';
import { DeltaValue } from './parts/DeltaValue';

type Props = {
  /** Зона качества блока: все его критерии принадлежат ей. */
  zone: QualityZoneColor;
  items: CritStatItem[];
};

/** Колонка мини-отчёта с критериями одной зоны качества (зелёная / жёлтая / красная). */
export const ZoneCriteriaColumn: FC<Props> = ({ zone, items }) => {
  const { t } = useTranslation('qualityDashboard');

  // Подписи зон — общие с виджетом «Зоны качества» той же страницы, чтобы мини-отчёт
  // называл зоны так же, как остальной дашборд.
  const zoneLabel: Record<QualityZoneColor, string> = {
    green: t('zones.green'),
    yellow: t('zones.yellow'),
    red: t('zones.red'),
  };

  return (
    <div className="border rounded-xl p-4">
      <h4 className="font-medium mb-3">{zoneLabel[zone]}</h4>
      <div className="flex flex-col gap-2">
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground">{t('consultantReport.zoneEmpty')}</div>
        ) : (
          // Все критерии блока принадлежат его зоне, поэтому карточка окрашивается цветом зоны блока.
          items.map((item) => {
            // Отклонение балла от максимума шкалы — та величина, по которой определена зона критерия;
            // показывается рядом с отклонением от команды, чтобы дать оба сравнения сразу.
            const maxDiff =
              item.consultantAvg !== null && item.maxScore !== null
                ? item.consultantAvg - item.maxScore
                : null;
            return (
              // Ключ — пара «заключение + имя»: одноимённые критерии разных заключений различны.
              <div
                key={criterionKey(item.expertReportId, item.name)}
                className={cn('border rounded-lg p-3', zoneCardClass[zone])}
              >
                <div className="font-medium">{item.name}</div>
                <div className="text-sm text-muted-foreground">
                  {t('consultantReport.score')}: <strong>{fmt(item.consultantAvg)}</strong>
                  {item.teamAvg !== null && (
                    <>
                      {' '}
                      · {t('consultantReport.team')}: <strong>{fmt(item.teamAvg)}</strong>
                    </>
                  )}
                </div>
                {/* Два отклонения отдельными строками: от команды и от максимума шкалы */}
                <div className="mt-1 flex flex-col gap-0.5 text-sm text-muted-foreground">
                  <div>
                    {t('consultantReport.criterionTeamDeviation')}: <DeltaValue value={item.diff} />
                  </div>
                  <div>
                    {/* Максимум шкалы критерия показан в скобках рядом с подписью — по нему считается отклонение и зона. */}
                    {t('consultantReport.criterionMaxDeviation', { max: fmt(item.maxScore, 0) })}:{' '}
                    <DeltaValue value={maxDiff} />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
